import styled from "@emotion/styled";
import { motion } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { sizes } from "../../../constants/spacing";
import { MenuItemAppearances, MenuLayoutOptions } from "../types/Menu";
import { menuTheme } from "./menuTheme";

export interface MenuItemStyledBaseProps {
    appearance: MenuItemAppearances;
    isDisabled: boolean;
    isKeyboardNav: boolean | undefined;
    layout: MenuLayoutOptions;
    themeMode: RequiredThemeKeys;
}

export const focusContentAfterStyles = `
    border-radius: 0;
    &:after {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        width: 3px;
        height: 100%;
        opacity: 0;
        background: ${color("color--background--primary--default", "#2D60D7")};
    }
`;

/**
 * Handles the most basic styling of a menu item:
 *  - how it sits in its container
 *  - colors
 *  - padding/margins
 *  - focus states
 *  - hover states
 *  - disabled states
 *  - etc.
 *
 * To provide maximum flexibility, this component should NOT add styles around
 * how its contents are laid out or the spacing between any of them.
 *
 */
export const MenuItemBaseStyled = styled("button")<MenuItemStyledBaseProps>(
    ({ themeMode, isDisabled, layout, isKeyboardNav, appearance }) => `
            background-color: ${menuTheme.backgroundColor.rest(themeMode)};
            position: relative;
            // min-height to allow growing for metadata items, or other customizations.
            min-height: ${menuTheme.optionHeight[layout](themeMode)}px;
            border: 0;
            margin-top: 0;
            margin-right: ${layout === "dense" ? `0` : sizes.xSmall}px;
            margin-bottom:  var(--sds--menu--gap--${layout}, 0);
            margin-left: ${layout === "dense" ? `0` : sizes.xSmall}px;
            padding-top: var(--sds--menu-item--padding-y--${layout}, 0);
            padding-bottom: var(--sds--menu-item--padding-y--${layout}, 0);
            padding-left: var(--sds--menu-item--padding-x--${layout}, ${
        layout === "dense" ? sizes.small : sizes.xSmall
    }px);
            padding-right: var(--sds--menu-item--padding-x--${layout}, ${
        layout === "dense" ? sizes.small : sizes.xSmall
    }px);
            text-align: left;
            font-weight: 500;
            color: ${menuTheme.fontColor[appearance].rest(themeMode)};
            --sds--ursa-icons--text-color: ${menuTheme.fontColor[appearance].rest(themeMode)};
            border-radius: 0;
            transition: ${menuTheme.motion.itemBackground(themeMode)};

            &:focus-visible {
                outline: none;
            }
                
            ${focusContentAfterStyles}
            &:after{
                transition: ${motion.animation.default};
                // this is overriding the styles defined above in focusContentAfterStyles to scope to menu
                background-color: ${menuTheme.focusContentAfterStyles[appearance](themeMode)};
            }

            ${
                !isKeyboardNav && !isDisabled
                    ? `   
                &:hover {
                    background-color: ${menuTheme.backgroundColor[appearance].hover(themeMode)};
                    color: ${menuTheme.fontColor[appearance].hover(themeMode)};
                    --sds--ursa-icons--text-color: ${menuTheme.fontColor[appearance].hover(themeMode)};
                    &:after{
                        opacity: 1;
                        transition: ${motion.animation.default};
                    }
                }
            }`
                    : ``
            }

            &:focus, &[aria-expanded="true"] {
                background-color: ${menuTheme.backgroundColor[appearance].hover(themeMode)};
                color: ${menuTheme.fontColor[appearance].hover(themeMode)};
                --sds--ursa-icons--text-color: ${menuTheme.fontColor[appearance].hover(themeMode)};

                ${
                    !isDisabled
                        ? `&:after{
                        opacity: 1;
                        transition: ${motion.animation.default};
                    }`
                        : ``
                }
            }

            &:active {
                background-color: ${menuTheme.backgroundColor[appearance].pressed(themeMode)};
                color: ${menuTheme.fontColor[appearance].focus(themeMode)};
                --sds--ursa-icons--text-color: ${menuTheme.fontColor[appearance].focus(themeMode)};

            }

            ${
                isDisabled
                    ? `
                    opacity: 0.5;
                &:hover {
                    background-color: transparent;                    
                }

                &:focus {
                    opacity: 0.5;
                }
            `
                    : ``
            }

            &[aria-disabled="true"] {
                cursor: not-allowed;
            }

            // assists in rendering reasonably laid-out children.
            display: flex;
            flex-direction: row;
            align-items: center;

            // Reset ua stylesheet.
            font-family: inherit;
            font-size: ${menuTheme.fontSize(themeMode)};
            cursor: pointer;

            // For the highlight ::after position: absolute.
            position: relative;

            ::before {
                z-index: 2;
            }

            & > * {
                z-index: 2;
                // To allow the z-index to compare against the absolute ::before.
                position: relative;
            }
        `
);
MenuItemBaseStyled.displayName = "MenuItemBaseStyled";
